'use client'

import { useCallback, useEffect, useState } from 'react'

export interface GalleryStrings {
  label: string
  close: string
  prev: string
  next: string
}

export function Gallery({ images, title, strings }: {
  images: string[]
  title: string
  strings?: GalleryStrings
}) {
  const s = strings ?? { label: 'GALLERY', close: 'Close', prev: 'Previous image', next: 'Next image' }
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const close = useCallback(() => setOpenIndex(null), [])
  const prev = useCallback(() => setOpenIndex(i => (i === null ? i : (i - 1 + images.length) % images.length)), [images.length])
  const next = useCallback(() => setOpenIndex(i => (i === null ? i : (i + 1) % images.length)), [images.length])

  useEffect(() => {
    if (openIndex === null) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close()
      if (e.key === 'ArrowLeft') prev()
      if (e.key === 'ArrowRight') next()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [openIndex, close, prev, next])

  if (images.length <= 1) return null

  return (
    <section className="ed-section">
      <div className="ed-section-inner">
        <div className="ed-section-label">{s.label}</div>
        <div className="ed-gallery-grid">
          {images.map((src, i) => (
            <button
              type="button"
              key={src + i}
              className="ed-gallery-thumb"
              onClick={() => setOpenIndex(i)}
              aria-label={`View image ${i + 1} of ${title}`}
            >
              <img src={src} alt={`${title} — photo ${i + 1}`} loading="lazy" />
            </button>
          ))}
        </div>
      </div>

      {openIndex !== null && (
        <div className="ed-lightbox" role="dialog" aria-modal="true" onClick={close}>
          <button type="button" className="ed-lightbox-close" onClick={close} aria-label={s.close}>✕</button>
          {images.length > 1 && (
            <button
              type="button"
              className="ed-lightbox-nav ed-lightbox-nav--prev"
              onClick={e => { e.stopPropagation(); prev() }}
              aria-label={s.prev}
            >‹</button>
          )}
          <img
            src={images[openIndex]}
            alt={`${title} — photo ${openIndex + 1}`}
            className="ed-lightbox-img"
            onClick={e => e.stopPropagation()}
          />
          {images.length > 1 && (
            <button
              type="button"
              className="ed-lightbox-nav ed-lightbox-nav--next"
              onClick={e => { e.stopPropagation(); next() }}
              aria-label={s.next}
            >›</button>
          )}
          <div className="ed-lightbox-count">{openIndex + 1} / {images.length}</div>
        </div>
      )}
    </section>
  )
}
